import {t, Trans} from '@lingui/macro'
import type {MouseEventHandler} from 'react'
import {useState} from 'react'

import {Box, Link, Loading, ModalClose, ModalContent, ModalTitle, tokens} from '@pleo-io/telescope'

import {useCompany} from '@product-web/api-deimos/company.v2'
import {BillingInterval, Interval} from '@product-web/api-types/billing'
import type {PlanType} from '@product-web/api-types/plan'
import type {Subscription} from '@product-web/bff-moons/generated/beyond'
import {ContactSupport} from '@product-web/feature-ui/contact-support'
import type Country from '@product-web/locale/country'
import {getIsCompanyOwner, useUser} from '@product-web/shell/user'

import tracking from '~/tracking'
import config from 'config'

import {getIsLastDay, getPlanModalTitle} from './choose-plan-container.helper'
import IntervalSelector from './interval-selector'
import {ModalFooterContainer, ModalHeader, ModalWrapper} from './modal-choose-plans.styles'
import type {PlanPrices} from './plan-options'
import PlanOptions from './plan-options'

import {useCountryPlans} from '../lib/country-plans'

export interface UpdatePlanProps {
    planType: PlanType
    interval?: Interval.MONTH | Interval.YEAR
    essentialPlanPrices?: PlanPrices
    advancedPlanPrices?: PlanPrices
    includeCashback?: Boolean
    contractIntervalCommitment: number
    setLoading: (isLoading: boolean) => void
}

export interface ChoosePlanModalProps {
    isDismissible?: boolean
    onDismiss: Function
    trialEndDate?: Date
    updatePlan: (updatePlanProps: UpdatePlanProps) => void
    hasFooter?: boolean
    subscription: Subscription | null
    country: Country
}

export const ChoosePlanModal = ({
    isDismissible,
    onDismiss,
    trialEndDate,
    updatePlan,
    hasFooter,
    subscription,
    country,
}: ChoosePlanModalProps) => {
    const user = useUser()
    const {data: plans, isLoading: isPlansLoading} = useCountryPlans()

    const isOwner = getIsCompanyOwner(user)
    const [loading, setLoading] = useState(false)
    const {data: company, error: companyError} = useCompany()
    const isCompanyLoading = !company && !companyError

    const [selectedCommitment, setSelectedCommitment] = useState<Interval.MONTH | Interval.YEAR>(
        Interval.MONTH,
    )
    const contractIntervalCommitment = selectedCommitment === Interval.MONTH ? 1 : 12

    const [selectedBilling, setSelectedBilling] = useState<BillingInterval>(BillingInterval.MONTHLY)

    const starterPlanUserLimit = 3

    const planModalTitle = getPlanModalTitle({trialEndDate})
    const isLastDay = getIsLastDay({trialEndDate})

    const onPlanDetailsClicked: MouseEventHandler<HTMLAnchorElement> = (event) => {
        tracking.pricingEnquiryLinkActioned({
            //eslint-disable-next-line string-to-lingui/missing-lingui-transformation
            location: 'choose plan modal footer',
            destination: event.currentTarget.href,
            source: window.location.href,
        })
    }

    const showCloseButton = isDismissible && !isLastDay

    return (
        <ModalWrapper
            data-testid="choose-plan-modal"
            style={{
                paddingBottom: hasFooter ? 0 : tokens.spacing20,
            }}
        >
            {showCloseButton && (
                <ModalClose data-testid="choose-plan-modal-close" onClick={() => onDismiss()} />
            )}
            <ModalHeader>
                <ModalTitle id="choose-plan-modal-header-text">{planModalTitle}</ModalTitle>
            </ModalHeader>
            <ModalContent>
                {isPlansLoading || isCompanyLoading || loading ? (
                    <Loading />
                ) : (
                    <Box>
                        <IntervalSelector
                            selectedCommitment={selectedCommitment}
                            onCommitmentChange={setSelectedCommitment}
                            onBillingChange={setSelectedBilling}
                        />
                        <PlanOptions
                            subscription={subscription}
                            starterPlanUserLimit={starterPlanUserLimit}
                            updatePlan={(planType, interval, planPrices) => {
                                setLoading(true)
                                updatePlan({
                                    planType,
                                    setLoading,
                                    interval,
                                    contractIntervalCommitment,
                                    essentialPlanPrices: planPrices?.essentialPlanPrices,
                                    advancedPlanPrices: planPrices?.advancedPlanPrices,
                                })
                            }}
                            isOwner={isOwner}
                            country={country}
                            planPrices={plans?.plansPrices}
                            onDismiss={onDismiss}
                            commitmentInterval={selectedCommitment}
                            billingInterval={selectedBilling}
                        />
                    </Box>
                )}
            </ModalContent>
            {hasFooter && (
                <ModalFooterContainer>
                    <Trans>Want to know more?</Trans>{' '}
                    <Link
                        href={`${config.endpoints.commercial}/pricing`}
                        target="_blank"
                        onClick={onPlanDetailsClicked}
                    >
                        <Trans>See plan details</Trans>
                    </Link>{' '}
                    <Trans>or</Trans>{' '}
                    <ContactSupport
                        onClick={tracking.choosePlanSupportChatOpened}
                        chatLabel={t`chat with us`}
                        emailLabel={t`Contact us to change your plan`}
                        emailSubject={t`I would like to change my plan`}
                    />
                </ModalFooterContainer>
            )}
        </ModalWrapper>
    )
}
